import { motion } from "framer-motion";
import { Link } from "react-router-dom";

import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";

const Section = ({ title, children }: { title: string; children: React.ReactNode }) => (
  <section className="mb-10">
    <h2 className="mb-4 font-display text-xl font-bold uppercase tracking-wider text-primary md:text-2xl">
      {title}
    </h2>
    <div className="space-y-3 text-sm leading-relaxed text-muted-foreground md:text-[15px]">
      {children}
    </div>
  </section>
);

const SubSection = ({ title, children }: { title: string; children: React.ReactNode }) => (
  <div className="mb-6">
    <h3 className="mb-2 font-display text-base font-bold uppercase tracking-wider text-foreground">
      {title}
    </h3>
    <div className="space-y-2">{children}</div>
  </div>
);

const List = ({ items }: { items: React.ReactNode[] }) => (
  <ul className="list-disc space-y-1.5 pl-5 marker:text-primary">
    {items.map((item, index) => (
      <li key={index}>{item}</li>
    ))}
  </ul>
);

const Datenschutz = () => {
  return (
    <div className="min-h-screen bg-background">
      <Navbar />
      <main>
        <section className="relative overflow-hidden pt-32 pb-12">
          <div className="absolute inset-0 bg-[radial-gradient(circle_at_50%_0%,hsl(210_100%_15%)_0%,transparent_60%)] opacity-60" />
          <div className="container relative mx-auto px-4 text-center">
            <motion.div initial={{ opacity: 0, y: 30 }} animate={{ opacity: 1, y: 0 }}>
              <span className="mb-4 block text-sm uppercase tracking-[0.3em] text-primary">
                Rechtliches
              </span>
              <h1 className="font-display text-5xl font-bold md:text-6xl">
                Daten<span className="text-primary">schutz</span>
              </h1>
              <p className="mt-4 text-sm text-muted-foreground">Stand: 16. April 2026</p>
            </motion.div>
          </div>
        </section>

        <section className="pb-24">
          <div className="container mx-auto px-4">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              className="relative mx-auto max-w-4xl border border-border bg-card p-6 md:p-12"
            >
            <div className="absolute -left-1 -top-1 h-4 w-4 border-l-2 border-t-2 border-primary" />
            <div className="absolute -right-1 -top-1 h-4 w-4 border-r-2 border-t-2 border-primary" />
            <div className="absolute -bottom-1 -left-1 h-4 w-4 border-b-2 border-l-2 border-primary" />
            <div className="absolute -bottom-1 -right-1 h-4 w-4 border-b-2 border-r-2 border-primary" />

            <Section title="Verantwortlicher">
              <p className="text-foreground">
                Giuseppe Romano
                <br />
                Betontankstelle Giuseppe Romano
                <br />
                Primsaue 5
                <br />
                66809 Nalbach
              </p>
              <p>
                Telefon:{" "}
                <a href="tel:+4915141315272" className="text-primary hover:underline">
                  0151 41315272
                </a>
                <br />
                E-Mail:{" "}
                <a href="mailto:info@beton2go-romano.de" className="text-primary hover:underline">
                  info@beton2go-romano.de
                </a>
              </p>
            </Section>

            <Section title="Zweck und Umfang der Verarbeitung">
              <p>
                Wir verarbeiten personenbezogene Daten nur insoweit, wie dies für den Betrieb dieser
                Website, die Bearbeitung von Anfragen und die Bereitstellung ausdrücklich gewünschter
                Funktionen erforderlich ist.
              </p>
              <List
                items={[
                  "Bereitstellung und sichere Auslieferung der Website",
                  "Bearbeitung von Kontaktanfragen per E-Mail oder Telefon",
                  "Speicherung Ihrer Datenschutzeinstellungen",
                  "Einbindung der Google-Maps-Karte nur nach Ihrer Einwilligung",
                ]}
              />
            </Section>

            <Section title="Bereitstellung der Website und Hosting">
              <p>
                Beim Aufruf dieser Website verarbeitet unser Hosting-Provider technisch erforderliche
                Verbindungsdaten, damit die Seite ausgeliefert und die Systemsicherheit gewährleistet
                werden kann.
              </p>
              <SubSection title="Verarbeitete Daten">
                <List
                  items={[
                    "IP-Adresse",
                    "Datum und Uhrzeit des Abrufs",
                    "angeforderte Datei bzw. URL",
                    "Browsertyp und Betriebssystem",
                    "Referrer-URL",
                    "Statuscodes und übertragene Datenmenge",
                  ]}
                />
              </SubSection>
              <SubSection title="Rechtsgrundlage">
                <p>Art. 6 Abs. 1 lit. f DSGVO. Unser berechtigtes Interesse liegt in der sicheren und stabilen Bereitstellung der Website.</p>
              </SubSection>
              <SubSection title="Hosting-Provider">
                <p>
                  Sofern unsere Website wie derzeit vorgesehen bei 1&amp;1 IONOS gehostet wird,
                  erfolgt die Verarbeitung durch:
                </p>
                <p>
                  1&amp;1 IONOS SE, Elgendorfer Str. 57, 56410 Montabaur, Deutschland
                  <br />
                  <a
                    href="https://www.ionos.de/terms-gtc/terms-privacy"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-primary hover:underline break-all"
                  >
                    ionos.de/terms-gtc/terms-privacy
                  </a>
                </p>
              </SubSection>
            </Section>

            <Section title="Kontaktaufnahme">
              <p>
                Wenn Sie uns per E-Mail oder Telefon kontaktieren, verarbeiten wir die von Ihnen
                mitgeteilten Daten ausschließlich zur Bearbeitung Ihrer Anfrage.
              </p>
              <SubSection title="Verarbeitete Daten">
                <List
                  items={[
                    "Name",
                    "Kontaktdaten",
                    "Inhalt Ihrer Nachricht",
                    "weitere freiwillig mitgeteilte Angaben",
                  ]}
                />
              </SubSection>
              <SubSection title="Rechtsgrundlagen">
                <List
                  items={[
                    "Art. 6 Abs. 1 lit. b DSGVO, soweit Ihre Anfrage auf einen Vertrag oder vorvertragliche Maßnahmen gerichtet ist",
                    "Art. 6 Abs. 1 lit. f DSGVO für allgemeine Anfragen und Kommunikation",
                  ]}
                />
              </SubSection>
            </Section>

            <Section title="Cookies, Local Storage und Consent-Management">
              <p>
                Diese Website setzt derzeit keine Analyse-, Marketing- oder Remarketing-Cookies ein.
                Wir speichern lediglich Ihre Auswahl zu optionalen Diensten lokal im Browser, damit
                Ihre Entscheidung respektiert und das Banner nicht bei jedem Seitenaufruf erneut
                angezeigt wird.
              </p>
              <SubSection title="Technisch notwendige Speicherung">
                <p>
                  Die Speicherung Ihrer Consent-Auswahl erfolgt ausschließlich zu dem Zweck, die von
                  Ihnen gewünschte Datenschutzeinstellung umzusetzen. Diese Speicherung ist technisch
                  erforderlich, damit wir den ausdrücklich gewünschten digitalen Dienst datenschutzkonform
                  bereitstellen können.
                </p>
              </SubSection>
              <SubSection title="Rechtsgrundlagen">
                <List
                  items={[
                    "§ 25 Abs. 2 Nr. 2 TDDDG für die technisch erforderliche Speicherung Ihrer Auswahl",
                    "Art. 6 Abs. 1 lit. f DSGVO für die Umsetzung und Dokumentation Ihrer Datenschutzeinstellung",
                  ]}
                />
              </SubSection>
              <SubSection title="Widerruf und Änderung">
                <p>
                  Sie können Ihre Auswahl jederzeit über die Seite{" "}
                  <Link to="/cookies" className="text-primary hover:underline">
                    Cookies
                  </Link>{" "}
                  oder den Footer-Link „Cookies“ ändern oder widerrufen.
                </p>
              </SubSection>
            </Section>

            <Section title="Google Maps">
              <p>
                Auf unserer Website ist eine Google-Maps-Karte eingebunden. Die Karte wird erst
                geladen, wenn Sie der Kategorie „Externe Medien“ ausdrücklich zugestimmt haben.
                Vorher bleibt der Bereich blockiert und es wird keine Verbindung zu Google-Servern
                hergestellt.
              </p>
              <SubSection title="Anbieter">
                <p>
                  Google Ireland Limited, Gordon House, Barrow Street, Dublin 4, Irland
                  <br />
                  Datenschutzhinweise von Google:{" "}
                  <a
                    href="https://policies.google.com/privacy"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-primary hover:underline break-all"
                  >
                    policies.google.com/privacy
                  </a>
                </p>
              </SubSection>
              <SubSection title="Verarbeitete Daten">
                <List
                  items={[
                    "IP-Adresse",
                    "Browser- und Gerätedaten",
                    "Nutzungsdaten zur Kartenanzeige",
                    "gegebenenfalls Standortbezug, sofern Sie diesen gegenüber Google freigeben",
                  ]}
                />
              </SubSection>
              <SubSection title="Rechtsgrundlagen">
                <List
                  items={[
                    "§ 25 Abs. 1 TDDDG",
                    "Art. 6 Abs. 1 lit. a DSGVO",
                  ]}
                />
              </SubSection>
              <SubSection title="Drittlandtransfer">
                <p>
                  Es kann nicht ausgeschlossen werden, dass im Zusammenhang mit Google Maps Daten
                  auch an Google LLC in den USA übermittelt werden. Grundlage hierfür kann der
                  Angemessenheitsbeschluss der EU-Kommission vom 10. Juli 2023 zum EU-U.S. Data
                  Privacy Framework sein, sofern der jeweilige Empfänger darunter zertifiziert ist.
                  Ergänzend können Standardvertragsklauseln eingesetzt werden.
                </p>
              </SubSection>
            </Section>

            <Section title="Google Fonts">
              <p>
                Wir laden keine Google Fonts von Servern von Google oder anderen Drittanbietern.
                Die Website verwendet ausschließlich lokal verfügbare bzw. über den eigenen Webserver
                bereitgestellte Schriftarten. Beim Seitenaufruf wird daher keine Schriftarten-Anfrage
                an Google ausgelöst.
              </p>
            </Section>

            <Section title="Externe Links">
              <p>
                Unsere Website enthält externe Links, beispielsweise zu Google Maps. Wenn Sie einen
                solchen Link anklicken, verlassen Sie unsere Website. Für die Datenverarbeitung auf
                der verlinkten externen Website ist ausschließlich der jeweilige Anbieter verantwortlich.
              </p>
            </Section>

            <Section title="Speicherdauer">
              <p>
                Personenbezogene Daten speichern wir nur so lange, wie dies für den jeweiligen Zweck
                erforderlich ist oder gesetzliche Aufbewahrungspflichten bestehen.
              </p>
              <List
                items={[
                  "Server-Logdaten nur für den zur Sicherstellung des Betriebs erforderlichen Zeitraum",
                  "Kontaktanfragen nur so lange, wie dies zur Bearbeitung und etwaigen Anschlusskommunikation erforderlich ist",
                  "Datenschutzeinstellungen bis zu einer Änderung oder Löschung durch Sie bzw. bis zu einer technischen Neufassung unseres Consent-Systems",
                ]}
              />
            </Section>

            <Section title="Ihre Rechte">
              <p>Sie haben insbesondere folgende Rechte nach der DSGVO:</p>
              <List
                items={[
                  "Auskunft nach Art. 15 DSGVO",
                  "Berichtigung nach Art. 16 DSGVO",
                  "Löschung nach Art. 17 DSGVO",
                  "Einschränkung der Verarbeitung nach Art. 18 DSGVO",
                  "Datenübertragbarkeit nach Art. 20 DSGVO",
                  "Widerspruch gegen Verarbeitungen auf Grundlage von Art. 6 Abs. 1 lit. f DSGVO nach Art. 21 DSGVO",
                  "Widerruf erteilter Einwilligungen mit Wirkung für die Zukunft",
                ]}
              />
            </Section>

            <Section title="Beschwerderecht bei der Aufsichtsbehörde">
              <p>
                Sie haben das Recht, sich bei einer Datenschutzaufsichtsbehörde zu beschweren. Für
                Unternehmen im Saarland ist zuständig:
              </p>
              <p className="text-foreground">
                Unabhängiges Datenschutzzentrum Saarland
                <br />
                Die Landesbeauftragte für Datenschutz und Informationsfreiheit
                <br />
                Fritz-Dobisch-Straße 12
                <br />
                66111 Saarbrücken
              </p>
              <p>
                <a
                  href="https://www.datenschutz.saarland.de/"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-primary hover:underline"
                >
                  datenschutz.saarland.de
                </a>
              </p>
            </Section>
            </motion.div>
          </div>
        </section>
      </main>

      <Footer />
    </div>
  );
};

export default Datenschutz;
